/**
 * Accounts Service
 * Handles all account-related API calls
 */

import network from '../../Network/NetworkManager';

const APPURL = require('../../Constants/URLConstants');

/**
 * Get all accounts
 * @param {object} params - Query parameters (page, per_page, type, status, search_key)
 * @returns {Promise<object>} - Response with accounts array
 */
export const getAccounts = async (params = {}) => {
  try {
    // Build query string with proper format
    const queryParams = new URLSearchParams({
      page: params.page || '1',
      per_page: params.per_page || '100',
      type: params.type || 'accounts',
      ...(params.status && { status: params.status }),
      ...(params.search_key && { search_key: params.search_key }),
    });

    const url = `${APPURL.URLPaths.accounts}?${queryParams.toString()}`;

    const response = await network.networkCall(
      url,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching accounts:', error);
    throw error;
  }
};

/**
 * Get single account by ID
 * @param {number} accountId - Account ID
 * @returns {Promise<object>} - Response with account object
 */
export const getAccountById = async (accountId) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.accounts}${accountId}`,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching account:', error);
    throw error;
  }
};

/**
 * Create an account from an admin form.
 * @param {object} accountData - Account fields accepted by the API
 * @returns {Promise<object>} - API response
 */
export const createAccount = async (accountData = {}) => {
  try {
    return await network.networkCall(
      APPURL.URLPaths.accounts,
      'POST',
      JSON.stringify({account: accountData}),
      '',
      '',
    );
  } catch (error) {
    console.error('Error creating account:', error);
    throw error;
  }
};

/**
 * Get active accounts (for dropdown)
 * @returns {Promise<Array>} - Array of active accounts
 */
export const getActiveAccounts = async () => {
  try {
    const response = await getAccounts({
      type: 'accounts',
      per_page: '100',
    });

    // Return accounts array from response
    if (response.status && response.data?.accounts) {
      return response.data.accounts;
    }

    return response.accounts || [];
  } catch (error) {
    console.error('Error fetching active accounts:', error);
    return [];
  }
};

/**
 * Search accounts by keyword
 * @param {string} searchKey - Search keyword
 * @returns {Promise<Array>} - Array of matching accounts
 */
export const searchAccounts = async (searchKey = '') => {
  try {
    const response = await getAccounts({
      type: 'accounts',
      per_page: '100',
      search_key: searchKey,
    });

    // Return accounts array from response
    if (response.status && response.data?.accounts) {
      return response.data.accounts;
    }

    return response.accounts || [];
  } catch (error) {
    console.error('Error searching accounts:', error);
    return [];
  }
};
